import { useCallback, useEffect, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./MantlePaywall.module.scss"

/**
 * MantlePaywall (`/mantle`) — an original, self-contained EXPIRATION paywall for
 * Private Photo Vault Premium.
 *
 * Divergence brief: claude-paywalls/_ledger.md. Concept doc: claude-paywalls/mantle.md.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * Newest @Paywalls-Bot human mention this run: `focus on security and privacy` (NOT
 * the reset keyword `default`) → an ACTIVE STANDING DIRECTIVE at super-high priority.
 * The whole concept is built to it — the pitch is restoring the private-SECURITY
 * protections that pause when Premium lapses.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "Draw the cover back over your memories." Premium lapsed, so the protective MANTLE
 * over your vault has thinned. Renew and a luminous cover draws back across your
 * photos, re-lighting each real security protection (encrypted backup, break-in
 * reports, decoy vault, app disguise) as it passes. The photos are shown intact and
 * locked the entire time — only the protection layer lapsed.
 *
 * ── Honest expiration framing (compliance) ─────────────────────────────────
 * On-screen: "Your photos never left the vault — they're still locked behind your PIN.
 * Renewing restores the protections that pause when Premium ends." NO data-loss claim,
 * no countdown, no fake scarcity, honest Save %. Yearly is featured & pre-selected.
 *
 * ── Divergence ─────────────────────────────────────────────────────────────
 * Layout   : horizontal COVERAGE BAND / timeline keep — a mosaic of real photos under a
 *            luminous mantle sheet, a 12-month coverage scale beneath. Not hero+card, not
 *            a console/plate, not a scattered field, not a pour, not a locksmith bench.
 * Interact : the PRICING CHOICE powers the coverage art — Yearly draws one continuous
 *            unbroken span; Monthly draws the same protection as 12 separated pips with
 *            re-arm seams. Plus an ambient draw-across on load (the "restore").
 * Metaphor : a MANTLE — a cloak of light drawn back over memories. Warm shelter.
 * Value    : ENDOWMENT / restore-what-was-yours × CONTRAST framing (continuous vs. re-armed).
 * Register : reassuring, warm, sheltering — "come back under cover."
 */

type PlanKey = "yearly" | "monthly"

// The four real Private Photo Vault Premium protections — the "threads" of the mantle.
// Each latches lit as the cover draws over its position along the band (0..1).
const THREADS: { k: string; label: string; at: number; icon: string }[] = [
  { k: "backup", label: "Encrypted backup", at: 0.14, icon: "backup" },
  { k: "breakin", label: "Break-in reports", at: 0.38, icon: "breakin" },
  { k: "decoy", label: "Decoy vault", at: 0.62, icon: "decoy" },
  { k: "disguise", label: "App disguise", at: 0.86, icon: "disguise" },
]

// Reuse bundled personal-memory photos (public/byheart). Shown in full colour and
// locked the whole time — the point is never to hide them, only to restore the cover.
const PHOTOS = ["m02", "m07", "m11", "m04", "m15", "m09"]

// What each thread actually does, in one honest line.
const THREAD_NOTE: Record<string, string> = {
  backup: "A private backup only you can open.",
  breakin: "A photo of anyone who enters the wrong PIN.",
  decoy: "A second vault that opens with a decoy PIN.",
  disguise: "Your vault hides as an everyday app.",
}

const clamp = (v: number, lo: number, hi: number) =>
  v < lo ? lo : v > hi ? hi : v

const ThreadIcon = ({ name }: { name: string }) => {
  switch (name) {
    case "backup":
      return (
        <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
          <path
            d="M6 10.5a6 6 0 0 1 11.6-1.7A4.2 4.2 0 0 1 17.5 17H7a4 4 0 0 1-1-7.5z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinejoin="round"
          />
          <path
            d="M12 10v5m0 0l-2-2m2 2l2-2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "breakin":
      return (
        <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
          <circle
            cx="12"
            cy="12"
            r="8.2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
          />
          <circle
            cx="12"
            cy="12"
            r="3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
          />
          <path
            d="M12 3.8v2.4M12 17.8v2.4M3.8 12h2.4M17.8 12h2.4"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        </svg>
      )
    case "decoy":
      return (
        <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
          <rect
            x="4.5"
            y="7"
            width="10"
            height="12.5"
            rx="2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
          />
          <path
            d="M9.5 4.5h10v12.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinejoin="round"
            opacity="0.55"
          />
        </svg>
      )
    default:
      return (
        <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
          <rect
            x="5"
            y="3"
            width="14"
            height="18"
            rx="3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
          />
          <circle
            cx="12"
            cy="8.5"
            r="2.2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
          />
          <path
            d="M8.5 16.5c.6-2 1.9-3 3.5-3s2.9 1 3.5 3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        </svg>
      )
  }
}

const MantlePaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = (() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 79
    return Math.round((1 - y / (m * 12)) * 100)
  })()

  const [plan, setPlan] = useState<PlanKey>("yearly")
  // coverage: 0 = thinned (lapsed), 1 = fully drawn back across the band.
  const [cov, setCov] = useState<number>(reduceMotion ? 1 : 0)
  const [showClose, setShowClose] = useState<boolean>(!!reduceMotion)

  const covRef = useRef(cov)
  covRef.current = cov
  const rafRef = useRef<number>(0)
  const timers = useRef<number[]>([])

  const stopRaf = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = 0
  }

  // Tween the coverage value to a target — the "draw the cover across" motion.
  const drawTo = useCallback(
    (target: number, dur: number) => {
      stopRaf()
      if (reduceMotion) {
        covRef.current = target
        setCov(target)
        return
      }
      const start = covRef.current
      let t0 = 0
      const step = (t: number) => {
        if (!t0) t0 = t
        const p = clamp((t - t0) / dur, 0, 1)
        // easeOutCubic — a settling draw
        const e = 1 - Math.pow(1 - p, 3)
        const v = start + (target - start) * e
        covRef.current = v
        setCov(v)
        if (p < 1) rafRef.current = requestAnimationFrame(step)
      }
      rafRef.current = requestAnimationFrame(step)
    },
    [reduceMotion],
  )

  // On load: draw the cover back across (the restore), reveal close after a beat.
  useEffect(() => {
    native.logMixpanelEvent("html_mantle_view")
    if (reduceMotion) {
      setShowClose(true)
      return
    }
    timers.current.push(window.setTimeout(() => drawTo(1, 1500), 520))
    timers.current.push(window.setTimeout(() => setShowClose(true), 2200))
    return () => {
      stopRaf()
      timers.current.forEach((id) => window.clearTimeout(id))
      timers.current = []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const choosePlan = (next: PlanKey) => {
    if (next === plan) return
    setPlan(next)
    native.logMixpanelEvent("html_mantle_plan", { plan: next })
    // Reward the featured choice with a quick re-draw shimmer of the cover.
    if (!reduceMotion && next === "yearly") {
      drawTo(0.62, 220)
      timers.current.push(window.setTimeout(() => drawTo(1, 620), 240))
    }
  }

  const selectedProduct = plan === "yearly" ? yearlyProduct : monthlyProduct
  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    native.logMixpanelEvent("html_mantle_subscribe", { plan })
    if (selectedProduct) native.subscribe(selectedProduct)
  }
  const openTerms = () => native.postNativeEvent("terms")
  const openPrivacy = () => native.postNativeEvent("privacy")
  const handleRestore = () => native.postNativeEvent("already_purchased")

  const sealed = cov >= 0.999
  const pct = Math.round(cov * 100)

  // 12 month cells for the coverage scale under the band.
  const months = Array.from({ length: 12 }, (_, i) => i)

  return (
    <div className={`${styles.root} ${sealed ? styles.rootSealed : ""}`}>
      <div className={styles.aura} aria-hidden="true" />

      {/* ---------- header ---------- */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="11.4" r="2.1" fill="currentColor" />
            </svg>
          </span>
          <span className={styles.brandText}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
          onClick={handleClose}
          aria-label="Close"
          tabIndex={showClose ? 0 : -1}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* ---------- title ---------- */}
      <div className={styles.intro}>
        <span className={styles.eyebrow}>
          <span className={styles.eyebrowDot} aria-hidden="true" />
          Premium paused · protections thinned
        </span>
        <h1 className={styles.title}>
          Draw the cover back
          <br />
          over your memories.
        </h1>
        <p className={styles.sub}>
          Your photos never left the vault — they’re still locked behind your
          PIN. Renewing restores the protections that pause when Premium ends.
        </p>
      </div>

      {/* ---------- coverage band ---------- */}
      <div
        className={styles.band}
        role="group"
        aria-label="Coverage over your private photos"
      >
        {/* live readout */}
        <div className={styles.readout}>
          <span
            className={`${styles.readoutDot} ${sealed ? styles.readoutDotOn : ""}`}
            aria-hidden="true"
          />
          <span className={styles.readoutLabel} aria-live="polite">
            {sealed ? "COVER RESTORED" : "RESTORING COVER"}
          </span>
          <span className={styles.readoutMeta}>
            {plan === "yearly" ? (
              <>
                12 months<em>· unbroken</em>
              </>
            ) : (
              <>
                30-day cover<em>· re-armed monthly</em>
              </>
            )}
          </span>
        </div>

        {/* stage: mosaic + mantle + threads, precisely overlaid */}
        <div className={styles.stage}>
          {/* the four protection threads, latching lit as the cover passes */}
          <div className={styles.threads} aria-hidden="true">
            {THREADS.map((t) => {
              const on = cov >= t.at
              return (
                <span
                  key={t.k}
                  className={`${styles.thread} ${on ? styles.threadOn : ""}`}
                  style={{ left: `${t.at * 100}%` }}
                >
                  <span className={styles.threadIcon}>
                    <ThreadIcon name={t.icon} />
                  </span>
                  <span className={styles.threadLabel}>{t.label}</span>
                  <span className={styles.threadStem} />
                </span>
              )
            })}
          </div>

          {/* the photo mosaic (memories under cover) */}
          <div className={styles.keep} aria-hidden="true">
            {PHOTOS.map((img, i) => (
              <span
                key={img}
                className={styles.tile}
                style={{ backgroundImage: `url(/byheart/${img}.jpg)` }}
              >
                <i
                  className={styles.tileVeil}
                  style={{
                    // veil fades out for the portion of the band already covered
                    opacity:
                      clamp(
                        1 - (cov - i / PHOTOS.length) * PHOTOS.length,
                        0,
                        1,
                      ) * 0.32,
                  }}
                />
              </span>
            ))}

            {/* the mantle — a luminous cover drawn across to `cov` */}
            <div className={styles.mantle} style={{ width: `${cov * 100}%` }}>
              <span className={styles.mantleWeave} />
              <span className={styles.mantleEdge} />
            </div>

            {/* the lock badge — the vault is shut with or without Premium */}
            <span className={styles.lockBadge}>
              <svg
                viewBox="0 0 24 24"
                width="12"
                height="12"
                aria-hidden="true"
              >
                <path
                  d="M7 10V8a5 5 0 0 1 10 0v2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                />
                <rect
                  x="5"
                  y="10"
                  width="14"
                  height="9"
                  rx="2.2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                />
                <circle cx="12" cy="14.4" r="1.4" fill="currentColor" />
              </svg>
              Locked
            </span>
          </div>
        </div>

        {/* 12-month coverage scale — the plan-driven contrast */}
        <div
          className={`${styles.scale} ${plan === "monthly" ? styles.scaleMonthly : ""}`}
          aria-hidden="true"
        >
          {months.map((m) => {
            const cellStart = m / 12
            const cellFill = clamp((cov - cellStart) * 12, 0, 1)
            return (
              <span key={m} className={styles.cell}>
                <span
                  className={styles.cellFill}
                  style={{ transform: `scaleX(${cellFill})` }}
                />
              </span>
            )
          })}
        </div>
        <div className={styles.scaleCaption}>
          {plan === "yearly" ? (
            <>
              One decision — <strong>{pct}%</strong> of a full year sealed under
              continuous cover.
            </>
          ) : (
            <>Covered while active — the cover is re-armed every 30 days.</>
          )}
        </div>
      </div>

      {/* ---------- protection notes ---------- */}
      <ul className={styles.notes}>
        {THREADS.map((t) => {
          const on = cov >= t.at
          return (
            <li
              key={t.k}
              className={`${styles.note} ${on ? styles.noteOn : ""}`}
            >
              <span className={styles.noteCheck} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="12" height="12">
                  <path
                    d="M5 12.5l4 4 10-10.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className={styles.noteText}>
                <b>{t.label}</b>
                {THREAD_NOTE[t.icon]}
              </span>
            </li>
          )
        })}
      </ul>

      {/* ---------- pricing (yearly featured) ---------- */}
      <div className={styles.pricing}>
        <button
          className={`${styles.plan} ${styles.planYear} ${
            plan === "yearly" ? styles.planActive : ""
          }`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.ribbon}>SAVE {savingsPct}%</span>
          <span className={styles.planLeft}>
            <span className={styles.radio} aria-hidden="true" />
            <span className={styles.planMeta}>
              <span className={styles.planName}>12 months</span>
              <span className={styles.planNote}>
                7 days free · then {yearlyPerMonth}/mo
              </span>
            </span>
          </span>
          <span className={styles.planRight}>
            <span className={styles.planPrice}>{yearlyPrice}</span>
            <span className={styles.planUnit}>/year</span>
          </span>
        </button>

        <button
          className={`${styles.plan} ${styles.planMonth} ${
            plan === "monthly" ? styles.planActive : ""
          }`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planLeft}>
            <span className={styles.radio} aria-hidden="true" />
            <span className={styles.planName}>Monthly</span>
          </span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={handleSubscribe}>
        <span className={styles.ctaMain}>
          {plan === "yearly" ? "Restore & try 7 days free" : "Restore Premium"}
        </span>
        <span className={styles.ctaSub}>
          {plan === "yearly"
            ? `Then ${yearlyPrice}/year · cancel anytime`
            : `${monthlyPrice}/month · cancel anytime`}
        </span>
      </button>

      <div className={styles.legal}>
        <button onClick={openTerms}>Terms</button>
        <span aria-hidden="true">·</span>
        <button onClick={openPrivacy}>Privacy</button>
        <span aria-hidden="true">·</span>
        <button onClick={handleRestore}>Restore</button>
        <span aria-hidden="true">·</span>
        <span>Auto-renews until cancelled</span>
      </div>
    </div>
  )
}

export default MantlePaywall
